import React, { useEffect, useState } from "react";
import { IoIosArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { playSong } from '../../common/actions/common.actions';
import dashboardService from "../../../api/services/dashboardService";
import { useTranslation } from "react-i18next";
import { HiOutlineNewspaper } from "react-icons/hi2";

export const HotNews = ({ setIsLoading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [currentNews, setCurrentNews] = useState([]);

  const handleSongClick = () => {
    dispatch(playSong());
  };

  const handleBackClick = () => {
    handleSongClick();
    navigate('/dashboard');
  };

  const handleSeeMoreClick = (news) => {
    navigate(`/hot-news/${news.hotNewsId}`, { state: { news } });
  };

  useEffect(() => {
    setIsLoading(true);
    dashboardService.getHotNews()
      .then(response => response.json())
      .then(data => {
        if (data && data.payload) {
          setCurrentNews(data.payload);
          setIsLoading(false);
        }
      })
      .catch(err => {
        console.log(err);
      });
  }, []);

  return (
    <div className="w-full h-[100dvh] flex flex-col bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 text-gray-700 dark:text-white overflow-hidden relative">
      {/* Professional Header */}
      <div className="flex items-center py-6 px-6 bg-white/80 backdrop-blur-xl dark:bg-gray-900/80 border-b border-gray-200/30 dark:border-gray-700/30 shadow-sm">
        <button
          className="w-12 h-12 mr-4 flex items-center justify-center text-gray-700 dark:text-white hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-xl transition-all duration-300 hover:scale-105 shadow-sm"
          onClick={handleBackClick}
        >
          <IoIosArrowBack size={24} className="text-gray-700 dark:text-white" />
        </button>
        <div className="flex items-center">
          <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-red-700 rounded-xl flex items-center justify-center mr-4 shadow-lg">
            <HiOutlineNewspaper size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white">Hot News</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Latest sports updates</p>
          </div>
        </div>
      </div>

      {/* Enhanced Content Area */}
      <div className="flex flex-col flex-1 px-6 py-6 overflow-auto bg-gradient-to-br from-blue-50/50 via-white/50 to-purple-50/50 dark:from-gray-900/50 dark:via-gray-800/50 dark:to-gray-900/50">
        {/* Section Header */}
        <div className="mb-6 p-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
            <div className="w-1 h-6 bg-gradient-to-b from-red-600 to-red-700 rounded-full mr-3"></div>
            What happened this week
          </h2>
        </div>

        {/* News Cards */}
        <div className="space-y-6">
          {currentNews.map((news, index) => (
            <div 
              key={news.hotNewsId} 
              className="group cursor-pointer bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden"
              onClick={() => handleSeeMoreClick(news)}
            >
              {/* News Header */}
              <div className="p-6 pb-4">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white leading-7 flex-1 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors duration-300">
                    {news?.hotNewsTitle}
                  </h3>
                  <div className="ml-4 px-3 py-1 bg-gradient-to-r from-red-100 to-orange-100 dark:from-red-900/30 dark:to-orange-900/30 rounded-full">
                    <span className="text-xs font-semibold text-red-600 dark:text-red-400">HOT</span>
                  </div>
                </div>
              </div>

              {/* News Image */}
              {news.hotNewsFilePath && (
                <div className="px-6 pb-4">
                  <div className="relative overflow-hidden rounded-xl shadow-md group-hover:shadow-lg transition-shadow duration-300">
                    <img 
                      src={news.hotNewsFilePath} 
                      alt="News" 
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
              )}

              {/* News Content */}
              <div className="px-6 pb-6">
                <p className="text-gray-600 dark:text-gray-300 leading-7 text-justify mb-4">
                  {news?.hotNewsBody ? news.hotNewsBody.slice(0, 255) + '...' : ''}
                </p>
                
                {/* Read More Button */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span>Breaking News</span>
                  </div>
                  <div className="flex items-center text-red-600 dark:text-red-400 font-semibold group-hover:text-red-700 dark:group-hover:text-red-300 transition-colors duration-300">
                    <span className="text-sm mr-2">Read More</span>
                    <IoIosArrowBack size={16} className="rotate-180 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {currentNews.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center p-8 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <HiOutlineNewspaper size={32} className="text-gray-600 dark:text-gray-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-2">No News Available</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Check back later for the latest updates</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
